/**
 *  Action Constants
 */
import * as selectors from './selectors'
import { parseLinkHeader } from '../../../util/LinkParserHelper'
import { Dispatch, ExtraArgs, GetState } from '../../../types'
import { addToast } from '../../Toasts/actions'
import { Alert, isBasicAlert } from '../model'
import { Toast } from '../../Toasts/model'
import { AlertsFilters, hasFiltersChanged } from './model'
import FeatureFlagService from '../../../services/FeatureFlagService'
import FeatureFlags from '../../../types/featureflags'
import { selectors as authSelectors } from '../../../lifecycle/auth'

export const ChangeAlerts = 'Monitoring/Alerts/ChangeAlerts'
export const ChangeFilteredAlerts = 'Monitoring/Alerts/ChangeFilteredAlerts'
export const LoadingAlerts = 'Monitoring/Alerts/Loading'
export const AlertsChangeColumnSort = 'Monitoring/Alerts/ChangeColumnSort'
export const AlertSearched = 'Monitoring/Alerts/AlertSearched'
export const AlertChangedToggle = 'Monitoring/Alerts/ChangedToggle'
export const ChangeFilterValue = 'Monitoring/Alerts/ChangeFilterValue'
export const ChangeSearchInput = 'Monitoring/Alerts/ChangeSearchInput'
export const SaveNextLink = 'Monitoring/Alerts/SaveNextLink'
export const SaveFilteredNextLink = 'Monitoring/Alerts/SaveFilteredNextLink'

/**
 *  Action Type
 */
export type ChangeAlertsAction = {
  type: 'Monitoring/Alerts/ChangeAlerts'
  payload: {
    alerts: Alert[],
  }
}

export type ChangeFilteredAlertsAction = {
  type: 'Monitoring/Alerts/ChangeFilteredAlerts'
  payload: {
    filtered: Alert[]
  }
}

export type LoadingAlertsAction = {
  type: 'Monitoring/Alerts/Loading',
}

export type ChangeFilterValueAction = {
  type: 'Monitoring/Alerts/ChangeFilterValue'
  payload: {
    key: string,
    value: string
  }
}

export type AlertSearchedAction = {
  type: 'Monitoring/Alerts/AlertSearched',
}

export type ChangeColumnSortAction = {
  type: 'Monitoring/Alerts/ChangeColumnSort'
  payload: {
    columnName: string,
    sortDirection: string
  }
}

export type AlertChangedToggleAction = {
  type: 'Monitoring/Alerts/ChangedToggle'
  payload: {
    enabled: boolean
  }
}

export type ChangeSearchInputAction = {
  type: 'Monitoring/Alerts/ChangeSearchInput'
  payload: {
    searchInput?: string
  }
}

export type SaveNextLinkAction = {
  type: 'Monitoring/Alerts/SaveNextLink'
  payload: {
    nextLink?: string
  }
}

export type SaveFilteredNextLinkAction = {
  type: 'Monitoring/Alerts/SaveFilteredNextLink'
  payload: {
    filteredNextLink?: string
  }
}

/**
 *  Action Creators
 */
export const changeAlerts = (alerts: Alert[]): ChangeAlertsAction => ({
  type: ChangeAlerts,
  payload: {alerts: alerts}
})

export const changeFilteredAlerts = (filtered: Alert[]): ChangeFilteredAlertsAction => ({
  type: ChangeFilteredAlerts,
  payload: {filtered: filtered}
})

export const changeSearchInput = (searchInput?: string): ChangeSearchInputAction => ({
  type: ChangeSearchInput,
  payload: {searchInput}
})

export const saveNextLink = (nextLink?: string): SaveNextLinkAction => ({
  type: SaveNextLink,
  payload: {nextLink}
})

export const saveFilteredNextLink = (filteredNextLink?: string): SaveFilteredNextLinkAction => ({
  type: SaveFilteredNextLink,
  payload: {filteredNextLink}
})

export const changeFilterValue = (key: string, value: string) => (dispatch: Dispatch, getState: GetState) => {
  const stateFilters = selectors.getFilters(getState())
  if (stateFilters[key] !== value) {
    dispatch({type: ChangeFilterValue, payload: {key: key, value: value}})
  }
}

export const isLoading = () => (dispatch: Dispatch) => {
  dispatch({type: LoadingAlerts})
}

export const changeColumnSort = (column: string, ascending: boolean): ChangeColumnSortAction => ({
  type: AlertsChangeColumnSort,
  payload: {columnName: `Alert ${column}`, sortDirection: ascending ? 'Ascending' : 'Descending'}
})

export const changedToggle = (enabled: boolean): AlertChangedToggleAction => ({
  type: AlertChangedToggle,
  payload: {enabled}
})

export const alertSearchedAction = (): AlertSearchedAction => ({
  type: AlertSearched,
})

export const handleRedirectAlertLink = (basicAlertId?: string) => () => {
  window.location.hash = basicAlertId ? `/alerts/${basicAlertId}` : '/alerts'
}

export const fetchAllAlerts = (firstFetch?: boolean) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const nextLink = getState().monitoring.alert.nextLink

  if (firstFetch || nextLink) {
    dispatch(isLoading())
    const orgId = getState().monitoring.auth.profile.organizationId
    const filters = getState().monitoring.alert.filters
    // Remove 'type' if feature flag is off or user is not Titanium
    const isTitanium = authSelectors.isMonitoringCenterPremium(getState())
    const isFeatureFlagEnabled = FeatureFlagService.get(FeatureFlags.ALERTS_BASIC_ADVANCED_FILTER_ENABLED)
    let sanitizedFilters = filters
    if (!(isTitanium && isFeatureFlagEnabled)) {
      const { type, ...rest } = filters
      sanitizedFilters = rest
    }
    const link = !firstFetch ? nextLink : undefined

    api.ui(getState).fetchAlerts(orgId, sanitizedFilters, link).then((response: { headers: Headers, dataPromise: Promise<Alert[]> }) => {
      const linkHeader = parseLinkHeader(response.headers.get('link'))
      const nextLinkFromHeader = linkHeader ? linkHeader.next : undefined
      dispatch(saveNextLink(nextLinkFromHeader))

      response.dataPromise.then((newAlerts: Alert[]) => {
        const oldAlerts = getState().monitoring.alert.alerts
        let shouldReplaceOld = false

        if (oldAlerts.length > 0) {
          newAlerts.forEach(newAlert => {
            shouldReplaceOld = !!oldAlerts.find(oldAlert => oldAlert.alertId === newAlert.alertId)
          })
        }

        const alerts = shouldReplaceOld ? newAlerts : oldAlerts.concat(newAlerts)
        const isBasicAlertsEnabled = getState().monitoring.alerts.isBasicAlertsEnabled
        const resolvedAlerts = isBasicAlertsEnabled ? alerts : alerts.filter(alert => !isBasicAlert(alert))

        dispatch(changeAlerts(resolvedAlerts))
      })
    }).catch(() => {
      const oldAlerts = getState().monitoring.alert.alerts
      dispatch(changeAlerts(oldAlerts))
    })
  }
}

/**
 *
 * @param filters Alerts list filters
 * @param nextPage If user scrolls down, nextPage should be true. This means that no filters were changed and needs to fetch the next page
 */
export const fetchAlerts = (filters: AlertsFilters & {search?: string}, nextPage?: boolean) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const filteredNextLink = getState().monitoring.alert.filteredNextLink
  const stateFilters = getState().monitoring.alert.filters
  const haveFiltersChanged = hasFiltersChanged(stateFilters, filters)

  dispatch(isLoading())
  const orgId = getState().monitoring.auth.profile.organizationId
  // Remove 'type' if feature flag is off or user is not Titanium
  const isTitanium = authSelectors.isMonitoringCenterPremium(getState())
  const isFeatureFlagEnabled = FeatureFlagService.get(FeatureFlags.ALERTS_BASIC_ADVANCED_FILTER_ENABLED)
  let sanitizedFilters = filters
  if (!(isTitanium && isFeatureFlagEnabled)) {
    const { type, ...rest } = filters
    sanitizedFilters = rest
  }

  const resolvedNextLink = nextPage ? filteredNextLink : undefined

  api.ui(getState).fetchAlerts(orgId, sanitizedFilters, resolvedNextLink)
    .then((response: { headers: Headers, dataPromise: Promise<Alert[]> }) => {
    const linkHeader = parseLinkHeader(response.headers.get('link'))
    const nextLink = linkHeader ? linkHeader.next : undefined
    dispatch(saveFilteredNextLink(nextLink))

    response.dataPromise.then((newAlerts: Alert[]) => {
      const oldAlerts = getState().monitoring.alert.filtered
      let shouldReplaceOld = false

      if (oldAlerts.length > 0) {
        newAlerts.forEach(newAlert => {
          shouldReplaceOld = !!oldAlerts.find(oldAlert => oldAlert.alertId === newAlert.alertId)
        })
      }

      const alerts = shouldReplaceOld || haveFiltersChanged ? newAlerts : oldAlerts.concat(newAlerts)
      const isBasicAlertsEnabled = getState().monitoring.alerts.isBasicAlertsEnabled
      const resolvedAlerts = isBasicAlertsEnabled ? alerts : alerts.filter(alert => !isBasicAlert(alert))

      dispatch(changeFilteredAlerts(resolvedAlerts))
    })
  }).catch(() => {
    const oldFilteredAlerts = getState().monitoring.alert.filtered
    dispatch(changeFilteredAlerts(oldFilteredAlerts))
  })
}

export const filterWithSearch = (input?: string) => (dispatch: Dispatch, getState: GetState) => {
  dispatch(alertSearchedAction())
  const filters = selectors.getFilters(getState())

  if ((filters.search && !input) || (!filters.search && input) || (filters.search !== input)) {
    const newInput = input && input !== '' ? input : undefined
    const newFilters = {...filters, search: newInput}

    dispatch(fetchAlerts(newFilters))
    dispatch(changeSearchInput(newInput))
  }
}

export const filterChange = (key: string, value: string) => (dispatch: Dispatch, getState: GetState) => {
  const filters = selectors.getFilters(getState())

  if (filters[key] === value) {
    return
  }

  dispatch(fetchAlerts({
    ...filters,
    [key]: value,
  }))
  dispatch(changeFilterValue(key, value))
}

export const pauseAlert = (alertId: string, isPaused: boolean) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const initialFilteredAlerts = selectors.getFilteredAlerts(getState())
  const initialAlerts = selectors.getAlerts(getState())
  const resolvedInitialAlerts = initialFilteredAlerts.length > 0 ? initialFilteredAlerts : initialAlerts
  const alertsUpdating = updateAlertLoading(resolvedInitialAlerts, alertId)

  if (initialFilteredAlerts.length > 0) {
    dispatch(changeFilteredAlerts(alertsUpdating))
  } else {
    dispatch(changeAlerts(alertsUpdating))
  }

  const orgId = getState().monitoring.auth.profile.organizationId
  return api.ui(getState).pauseAlert(orgId, alertId).then(() => {
    dispatch(changedToggle(!isPaused))
    const filteredAlerts = selectors.getFilteredAlerts(getState())
    const alerts = selectors.getAlerts(getState())

    const resolvedAlerts = filteredAlerts.length > 0 ? filteredAlerts : alerts
    const newAlerts = resolvedAlerts.map(alert => {
      if (alert.alertId === alertId) {
        const newAlert = Object.assign({}, alert)
        newAlert.alertState = isPaused ? 'paused' : 'pending'
        newAlert.isLoading = false
        return newAlert
      } else {
        return alert
      }
    })

    if (filteredAlerts.length > 0) {
      dispatch(changeFilteredAlerts(newAlerts))
    } else {
      dispatch(changeAlerts(newAlerts))
    }
  }).catch((error) => {
    const toast: Toast = {
      title: getPauseBasicAlertErr(error.errorCode),
      kind: 'error',
      testId: 'alert-state-change-error-toast'
    }
    dispatch(addToast(toast))
  })
}

const updateAlertLoading = (alertList: Alert[], alertId: string) => {
  return alertList.map((alert: Alert) => {
    if (alert.alertId === alertId) {
      const newAlert = Object.assign({}, alert)
      newAlert.isLoading = alert.alertId === alertId
      return newAlert
    } else {
      return alert
    }
  })
}

export const getPauseBasicAlertErr = (errorCode: number): string => {
  switch (errorCode) {
    case 403:
      return 'You have reached the limit of active API alerts. Please try pausing an alert and retry.'

    default:
      return `There was a problem when changing your alert's state. Try again later.`
  }
}

export type AlertActions =
  | ChangeAlertsAction
  | ChangeFilteredAlertsAction
  | ChangeSearchInputAction
  | LoadingAlertsAction
  | ChangeFilterValueAction
  | SaveNextLinkAction
  | SaveFilteredNextLinkAction
  | ChangeColumnSortAction
  | AlertSearchedAction
